import { useRef } from "react"

export type PrimaryClickEvent = Pick<
  MouseEvent,
  "metaKey" | "ctrlKey" | "shiftKey" | "button"
>

export function isPrimaryUnmodifiedClick(event: PrimaryClickEvent) {
  return (
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    event.button === 0
  )
}

export interface UseDragAwareClickOptions {
  /** Callback to invoke when a non-drag click occurs */
  onClick?: (event: React.MouseEvent) => void
  /** Pixel threshold to detect a meaningful drag. Default: 5 */
  dragThreshold?: number
}

export interface DragAwareClickHandlers {
  onPointerDown: (event: React.PointerEvent) => void
  onPointerMove: (event: React.PointerEvent) => void
  onClick: (event: React.MouseEvent) => void
}

/**
 * Hook that provides drag-aware click detection.
 * Prevents onClick from firing if the pointer moved significantly before the click.
 * Useful for contexts where clicks should be suppressed if dragging.
 *
 * @param options - Configuration for drag detection and click handling
 * @returns Object with event handlers to spread on an element
 *
 * @example
 * ```tsx
 * const clickHandlers = useDragAwareClick({
 *   onClick: () => closeModal(),
 *   dragThreshold: 5
 * })
 *
 * return <div {...clickHandlers}>Content</div>
 * ```
 */
export function useDragAwareClick(
  options: UseDragAwareClickOptions = {}
): DragAwareClickHandlers {
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null)
  const hasDraggedRef = useRef(false)
  const dragThreshold = options.dragThreshold ?? 5

  const handlePointerDown = (event: React.PointerEvent) => {
    pointerStartRef.current = { x: event.clientX, y: event.clientY }
    hasDraggedRef.current = false
  }

  const handlePointerMove = (event: React.PointerEvent) => {
    if (!pointerStartRef.current) return

    const deltaX = Math.abs(event.clientX - pointerStartRef.current.x)
    const deltaY = Math.abs(event.clientY - pointerStartRef.current.y)
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY)

    if (distance > dragThreshold) {
      hasDraggedRef.current = true
    }
  }

  const handleClick = (event: React.MouseEvent) => {
    // Only invoke onClick if no drag preceded this click
    if (!hasDraggedRef.current) {
      options.onClick?.(event)
    }

    // Reset for next interaction
    pointerStartRef.current = null
    hasDraggedRef.current = false
  }

  return {
    onPointerDown: handlePointerDown,
    onPointerMove: handlePointerMove,
    onClick: handleClick
  }
}
